import { useEffect, useState } from 'react'
import useNotifications from './useNotifications'
import type { UserNotification } from '../types/gotypes'

// A release tells the owners of live chats a few minutes ahead with a
// `release_pending` bell row (POST /internal/agent-sessions/release-notice).
// The chat page reads the same feed as the bell (TanStack shares the query)
// and, while such a row is recent, shows its message above the composer and
// tells the session hook to say "Platinum is updating" while it reconnects.

export const RELEASE_PENDING_TYPE = 'release_pending'

/** A notice older than this is over: the release has been and gone. */
export const RELEASE_NOTICE_MAX_AGE_MS = 20 * 60 * 1000

/** The session hook's activity label while the server is away during a release. */
export const RELEASE_RECONNECT_LABEL = 'Platinum is updating — your chat will carry on'

const FALLBACK_MESSAGE = 'Platinum updates in a few minutes. Your chat carries on; an answer in progress may pause briefly.'

/** The newest release_pending row younger than RELEASE_NOTICE_MAX_AGE_MS, as the text to show. */
export function activeReleaseNotice(notifications: UserNotification[], now: number): string | undefined {
  let newest: UserNotification | undefined
  for (const n of notifications) {
    if (n.type !== RELEASE_PENDING_TYPE) continue
    // A row from a server clock slightly ahead of this one counts as new.
    if (now - n.created_at >= RELEASE_NOTICE_MAX_AGE_MS) continue
    if (!newest || n.created_at > newest.created_at) newest = n
  }
  if (!newest) return undefined
  return newest.payload?.message || FALLBACK_MESSAGE
}

/** The release notice for this customer's chat page, and the reconnect label to use while it stands. */
export default function useReleaseNotice(customer: string): { text?: string; reconnectLabel?: string } {
  const { notifications } = useNotifications({ customer })
  const hasReleaseRow = notifications.some(n => n.type === RELEASE_PENDING_TYPE)
  const [now, setNow] = useState(() => Date.now())

  // The feed keeps the same array while its data is unchanged, so the age
  // check needs a clock of its own to let a notice lapse. It ticks only while
  // a release row is in the feed.
  useEffect(() => {
    if (!hasReleaseRow) return
    const timer = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(timer)
  }, [hasReleaseRow])

  const text = activeReleaseNotice(notifications, now)
  return { text, reconnectLabel: text ? RELEASE_RECONNECT_LABEL : undefined }
}
